import { createRouter, createWebHistory } from "vue-router";
import { datadogRum } from "@datadog/browser-rum";

import cognito from "@/configuration/cognito";
import store from "@/stores";

import activation from "@/routes/activation";
import administration from "@/routes/administration";
import business from "@/routes/business";
import member from "@/routes/member";
import signIn from "@/routes/signIn";
import signUp from "@/routes/signUp";
import signOut from "@/routes/signOut";
import user from "@/routes/user";
import pinMode from "@/routes/pinMode";
import unsubscribe from "@/routes/unsubscribe";
import memberInvite from "@/routes/memberInvite";
import prescription from "@/routes/prescription";
import pdf from "@/routes/pdf";

// add datadogRum only in production and development
if (window.env.environment !== "local") {
  datadogRum.init({
    applicationId: "5b87b7b3-2c57-4bad-bf30-0f3a0d001780",
    clientToken: "pub548c8195bde1fbfacca01ab708b7f988",
    site: "datadoghq.com",
    service: "predictile-webapp",
    env: window.env.environment,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
    allowedTracingUrls: [process.env.BASE_URL],
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: "/",
      component: () => import("@/views/HomeView.vue"),
      meta: {
        authentication: { required: true },
      },
    },
    ...activation,
    ...signUp,
    ...signIn,
    ...signOut,
    ...business,
    ...administration,
    ...member,
    ...user,
    ...pinMode,
    ...unsubscribe,
    ...memberInvite,
    ...prescription,
    ...pdf,
    {
      path: "/not-authorized",
      component: () => import("@/views/errors/NotAuthorizedView.vue"),
      meta: {
        useNewContainer: true,
        showNavShadow: true,
      },
    },
    {
      path: "/login-error",
      component: () => import("@/views/errors/LoginErrorView.vue"),
      meta: {
        useNewContainer: true,
        showNavShadow: true,
        preventInit: true,
        hideNav: true,
        authentication: { required: false },
      },
    },
    {
      path: "/:pathMatch(.*)*",
      component: () => import("@/views/errors/NotFoundView.vue"),
      meta: {
        useNewContainer: true,
        showNavShadow: true,
      },
    },
  ],
});

router.afterEach((to) => {
  datadogRum.startView(to.name);
});

router.beforeEach(async (to, from, next) => {
  const authentication = to.meta.authentication;
  const authorization = to.meta.authorization;
  const isAuthenticated = store.getters["cognito/isAuthenticated"];

  // stores
  if (isAuthenticated && !to.meta.preventInit) {
    if (!store.getters["organization/organization"]) await store.dispatch("organization/init");
    if (!store.getters["role/roles"]) await store.dispatch("role/init");
    if (to.params.businessId) await store.dispatch("role/loadPermissions", to.params.businessId);
  }

  // authentication
  if (authentication) {
    if (!isAuthenticated && authentication.required) {
      let url = "/signin?returnUrl=" + encodeURIComponent(to.fullPath);
      if (authentication.redirectMessage) url += "&m=" + encodeURIComponent(authentication.redirectMessage);
      if (to.query.cobrand) url += `&cobrand=${to.query.cobrand}`;
      next(url);
      return;
    }
    if (isAuthenticated && authentication.redirectAuthenticated) {
      next(authentication.redirectAuthenticated);
      return;
    }
    if (isAuthenticated && authentication.signoutAuthenticated) {
      cognito.signOut({ onComplete: () => next(true) });
      return;
    }
  }

  // authorization
  if (authorization) {
    const organization = store.getters["organization/organization"];
    const isPredictile = store.getters["role/isPredictile"]();
    const hasRole = store.getters["role/hasRole"];

    if (authorization.roles) {
      if (!organization) {
        next("/login-error");
        return;
      }
      const segment = to.params.businessId ? to.path.split(`/businesses/${to.params.businessId}/`)[1]?.split("/")[0] : null;
      const matrixGovernsThisPage = segment && store.getters["role/isPageGoverned"](segment);
      if (!matrixGovernsThisPage && !isPredictile && !hasRole(organization.id, authorization.roles)) {
        next("/not-authorized");
        return;
      }
    }
    if (authorization.requiresCapability) {
      const can = store.getters["role/can"];
      if (!can(authorization.requiresCapability)) {
        next("/not-authorized");
        return;
      }
    }
  }

  if (isAuthenticated && to.params.businessId) {
    const relative = to.path.split(`/businesses/${to.params.businessId}/`)[1];
    const parts = relative ? relative.split("/") : [];
    let segment = parts[0] || null;
    // Prefer a more specific governed page code for sub-pages (e.g. "business/profile" over "business").
    const twoSegment = parts.length > 1 ? `${parts[0]}/${parts[1]}` : null;
    if (twoSegment && store.getters["role/isPageGoverned"](twoSegment)) {
      segment = twoSegment;
    } else if (segment === "members" && relative.includes("members/")) {
      segment = "members/profile";
    }
    if (segment && store.getters["role/isPageGoverned"](segment) && !store.getters["role/can"](segment)) {
      next("/not-authorized");
      return;
    }
  }

  next(true);
});

export default router;
